import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Clock, Plus, AlertCircle, Bell } from 'lucide-react';
import Button from '../Common/Button';

const RemindersContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.bg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;



const RemindersList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ReminderCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme, priority }) => {
    switch (priority) {
      case 'high': return theme.colors.status.error;
      case 'medium': return theme.colors.accent.purple;
      case 'low': return theme.colors.primary[500];
      default: return theme.colors.dark.border;
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${({ theme, priority }) => {
      switch (priority) {
        case 'high': return theme.colors.status.error;
        case 'medium': return theme.colors.accent.purple;
        case 'low': return theme.colors.primary[500];
        default: return theme.colors.dark.border;
      }
    }};
    border-radius: 2px 0 0 2px;
  }
`;

const ReminderHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ReminderIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme, priority }) => {
    switch (priority) {
      case 'high': return `${theme.colors.status.error}20`;
      case 'medium': return `${theme.colors.accent.purple}20`;
      case 'low': return `${theme.colors.primary[500]}20`;
      default: return `${theme.colors.gray[500]}20`;
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme, priority }) => {
      switch (priority) {
        case 'high': return theme.colors.status.error;
        case 'medium': return theme.colors.accent.purple;
        case 'low': return theme.colors.primary[500];
        default: return theme.colors.gray[500];
      }
    }};
  }
`;

const ReminderContent = styled.div`
  flex: 1;
`;

const ReminderTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ReminderDescription = styled.p`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  line-height: 1.4;
`;

const ReminderTime = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  
  svg {
    width: 12px;
    height: 12px;
  }
`;



const PriorityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, priority }) => {
    switch (priority) {
      case 'high': return `${theme.colors.status.error}20`;
      case 'medium': return `${theme.colors.accent.purple}20`;
      case 'low': return `${theme.colors.primary[500]}20`;
      default: return `${theme.colors.gray[500]}20`;
    }
  }};
  color: ${({ theme, priority }) => {
    switch (priority) {
      case 'high': return theme.colors.status.error;
      case 'medium': return theme.colors.accent.purple;
      case 'low': return theme.colors.primary[500];
      default: return theme.colors.gray[500];
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const Reminders = () => {
  const [reminders, setReminders] = useState([
    {
      id: 1,
      title: 'Team Meeting',
      description: 'Weekly team sync-up meeting with development team',
      time: '2024-09-04T14:00:00',
      priority: 'high',
      category: 'work'
    },
    {
      id: 2,
      title: 'Doctor Appointment',
      description: 'Annual health checkup at Central Medical',
      time: '2024-09-05T10:30:00',
      priority: 'medium',
      category: 'personal'
    },
    {
      id: 3,
      title: 'Buy Groceries',
      description: 'Weekly grocery shopping - milk, bread, fruits',
      time: '2024-09-06T18:00:00',
      priority: 'low',
      category: 'personal'
    }
  ]);

  const getUpcomingReminders = () => {
    const now = new Date();
    return reminders.filter(reminder => {
      const reminderDate = new Date(reminder.time);
      return reminderDate >= now; // Only show current and upcoming reminders
    });
  };

  const formatReminderTime = (timeString) => {
    const reminderTime = new Date(timeString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    
    const isToday = reminderTime >= today && reminderTime < tomorrow;
    const isTomorrow = reminderTime >= tomorrow && reminderTime < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);
    
    let dateLabel;
    if (isToday) {
      dateLabel = 'Today';
    } else if (isTomorrow) {
      dateLabel = 'Tomorrow';
    } else {
      dateLabel = reminderTime.toLocaleDateString();
    }
    
    const timeLabel = reminderTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return { dateLabel, timeLabel };
  };

  const getReminderIcon = (priority) => {
    switch (priority) {
      case 'high': return AlertCircle;
      case 'medium': return Bell;
      case 'low': return Clock;
      default: return Clock;
    }
  };

  const upcomingReminders = getUpcomingReminders();

  return (
    <RemindersContainer>
      <Header>
        <HeaderLeft>
          <Title>Reminders</Title>
          {upcomingReminders.length > 0 && (
            <span style={{ 
              background: '#3b82f6', 
              color: 'white', 
              padding: '2px 8px', 
              borderRadius: '12px', 
              fontSize: '0.75rem',
              fontWeight: '600'
            }}>
              {upcomingReminders.length}
            </span>
          )}
        </HeaderLeft>
        
        <HeaderActions>
          <Button
            variant="primary"
            size="sm"
          >
            <Plus />
            Add Reminder
          </Button>
        </HeaderActions>
      </Header>

      <RemindersList>
        {upcomingReminders.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Clock />
            </EmptyIcon>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>No Upcoming Reminders</h3>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              You're all caught up! Add a reminder to get started.
            </p>
          </EmptyState>
        ) : (
          upcomingReminders.map((reminder) => {
            const IconComponent = getReminderIcon(reminder.priority);
            const { dateLabel, timeLabel } = formatReminderTime(reminder.time);
            
            return (
              <ReminderCard
                key={reminder.id}
                priority={reminder.priority}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <ReminderHeader>
                  <div style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                    <ReminderIcon priority={reminder.priority}>
                      <IconComponent />
                    </ReminderIcon>
                    
                    <ReminderContent>
                      <PriorityBadge priority={reminder.priority}>
                        {reminder.priority} priority
                      </PriorityBadge>
                      <ReminderTitle>{reminder.title}</ReminderTitle>
                      <ReminderDescription>{reminder.description}</ReminderDescription>
                      <ReminderTime>
                        <Clock />
                        {dateLabel} at {timeLabel}
                      </ReminderTime>
                    </ReminderContent>
                  </div>
                </ReminderHeader>
              </ReminderCard>
            );
          })
        )}
      </RemindersList>
    </RemindersContainer>
  );
};

export default Reminders;